import React, { useState, useEffect } from 'react';
import api from '../services/api';
import './UserManagement.css';
import ConfirmModal from './ConfirmModal';
import InputModal from './InputModal';

import toastr from 'toastr';
import 'toastr/build/toastr.min.css';
import './ConnectionStatus/ConnectionStatus.css';

const formatSubDate = (dateString) => {
    if (!dateString) return { text: 'Нет подписки', isExpired: true };

    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
        return { text: 'Некорректная дата подписки', isExpired: true };
    }

    const now = new Date();
    const isExpired = date <= now;

    const diffMs = Math.abs(date.getTime() - now.getTime());
    const totalHours = Math.floor(diffMs / (60 * 60 * 1000));
    const totalMinutes = Math.floor(diffMs / (60 * 1000));

    const days = Math.floor(totalHours / 24);
    const hours = totalHours % 24;
    const minutes = totalMinutes % 60;

    let extra = '';

    if (!isExpired) {
        if (days > 0) {
            extra = ` (осталось ~${days} дн ${hours} ч ${minutes} мин)`;
        } else if (hours > 0) {
            extra = ` (осталось ~${hours} ч ${minutes} мин)`;
        } else {
            extra = ` (осталось ~${minutes} мин)`;
        }

        return {
            text: `До ${date.toLocaleString('ru-RU')}${extra}`,
            isExpired: false
        };
    } else {
        if (days > 0) {
            extra = ` (~${days} дн ${hours} ч ${minutes} мин назад)`;
        } else if (hours > 0) {
            extra = ` (~${hours} ч ${minutes} мин назад)`;
        } else {
            extra = ` (~${minutes} мин назад)`;
        }

        return {
            text: `Истекла ${date.toLocaleString('ru-RU')}${extra}`,
            isExpired: true
        };
    }
};


function UserManagement() {
    const [users, setUsers] = useState([]);
    const [message, setMessage] = useState('');
    const [newLogin, setNewLogin] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [selectedUserIds, setSelectedUserIds] = useState(new Set());
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, message: '', onConfirm: null, danger: false });
    const [inputModal, setInputModal] = useState({ isOpen: false, title: '', fields: [], onSubmit: null });
    const [sessionsModal, setSessionsModal] = useState({
        isOpen: false,
        login: '',
        maxConcurrentSessions: 0,
        sessions: []
    });

    const fetchUsers = async () => {
        try {
            const response = await api.get('/admin/users');
            const sorted = (response.data || []).slice().sort((a, b) => {
                const now = Date.now();
                const aExp = a.subscriptionExpiresAt ? new Date(a.subscriptionExpiresAt).getTime() : 0;
                const bExp = b.subscriptionExpiresAt ? new Date(b.subscriptionExpiresAt).getTime() : 0;
                const aExpired = !aExp || aExp < now;
                const bExpired = !bExp || bExp < now;
                // Admins go to the bottom
                if (a.role === 'admin' && b.role !== 'admin') return 1;
                if (b.role === 'admin' && a.role !== 'admin') return -1;
                // Expired first
                if (aExpired && !bExpired) return -1;
                if (!aExpired && bExpired) return 1;
                // Among expired: most recently expired first
                // Among active: expiring soonest first
                return aExp - bExp;
            });
            setUsers(sorted);
        } catch (error) {
            setMessage('Failed to load users.');
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleCreateUser = async (e) => {
        e.preventDefault();
        try {
            await api.post('/admin/create-user', { login: newLogin, password: newPassword, role: 'user' });
            setMessage(`User ${newLogin} created successfully!`);
            setNewLogin('');
            setNewPassword('');
            fetchUsers();
        } catch (error) {
            setMessage(error.response?.data?.message || 'Failed to create user.');
        }
    };

    const handleUpdateLogin = (userId, currentLogin) => {
        setInputModal({
            isOpen: true,
            title: 'Изменить логин',
            fields: [{ name: 'login', label: 'Новый логин', defaultValue: currentLogin, autoFocus: true }],
            onSubmit: async ({ login }) => {
                setInputModal(p => ({ ...p, isOpen: false }));
                if (login && login !== currentLogin) {
                    try {
                        await api.put(`/admin/users/${userId}/login`, { newLogin: login });
                        fetchUsers();
                    } catch (error) {
                        toastr.warning(' ', error.response?.data?.message || 'Не удалось обновить логин.');
                    }
                }
            }
        });
    };

    const handleUpdatePassword = (userId) => {
        setInputModal({
            isOpen: true,
            title: 'Изменить пароль',
            fields: [{ name: 'password', label: 'Новый пароль', type: 'password', autoFocus: true }],
            onSubmit: async ({ password }) => {
                setInputModal(p => ({ ...p, isOpen: false }));
                if (password) {
                    try {
                        await api.put(`/admin/users/${userId}/password`, { newPassword: password });
                        toastr.success(' ', 'Пароль успешно обновлен.');
                    } catch (error) {
                        toastr.warning(' ', error.response?.data?.message || 'Не удалось обновить пароль.');
                    }
                }
            }
        });
    };

    const handleToggleChatAccess = async (userId, currentValue, login) => {
        try {
            const r = await api.setUserChatAccess(userId, !currentValue);
            toastr.success(' ', r.data?.message || `Доступ к чату обновлён.`);
            setUsers(prev => prev.map(u => u._id === userId ? { ...u, chatAccess: !currentValue } : u));
        } catch (error) {
            toastr.warning(' ', error.response?.data?.message || 'Не удалось обновить доступ к чату.');
        }
    };

    const handleToggleTransferAccess = async (userId, currentValue, login) => {
        try {
            const r = await api.setUserTransferAccess(userId, !currentValue);
            toastr.success(' ', r.data?.message || `Доступ к передаче устройств обновлён.`);
            setUsers(prev => prev.map(u => u._id === userId ? { ...u, canTransferDevices: !currentValue } : u));
        } catch (error) {
            toastr.warning(' ', error.response?.data?.message || 'Не удалось обновить доступ к передаче.');
        }
    };

    const handleSetFixedPrice = (userId, currentLogin, currentFixedPrice) => {
        const currentVal = (currentFixedPrice !== null && currentFixedPrice !== undefined) ? String(currentFixedPrice) : '';
        setInputModal({
            isOpen: true,
            title: `Фикс. цена для ${currentLogin} (0=зависимый, пусто=стандарт)`,
            fields: [{ name: 'price', label: 'Цена $/мес (пусто — сбросить)', defaultValue: currentVal, type: 'number', autoFocus: true }],
            onSubmit: async ({ price }) => {
                setInputModal(p => ({ ...p, isOpen: false }));
                const trimmed = price.trim();
                const val = trimmed === '' ? null : Number(trimmed);
                if (val !== null && isNaN(val)) {
                    toastr.warning(' ', 'Введите число или оставьте пустым.');
                    return;
                }
                try {
                    await api.put(`/admin/users/${userId}/special-conditions`, { fixedMonthlyPrice: val });
                    toastr.success(' ', val === null ? 'Фикс. цена сброшена.' : `Фикс. цена: $${val}/мес`);
                    fetchUsers();
                } catch (error) {
                    toastr.warning(' ', error.response?.data?.message || 'Не удалось обновить цену.');
                }
            }
        });
    };

    const handleToggleSessionLock = async (userId, currentLogin, isLocked) => {
        try {
            await api.put(`/admin/users/${userId}/special-conditions`, { sessionLocked: !isLocked });
            toastr.success(' ', isLocked ? `Блокировка сессий снята для ${currentLogin}` : `Сессии заблокированы для ${currentLogin}`);
            fetchUsers();
        } catch (error) {
            toastr.warning(' ', error.response?.data?.message || 'Не удалось обновить блокировку сессий.');
        }
    };

    const handleSetClientTag = (userId, currentLogin, currentTag) => {
        setInputModal({
            isOpen: true,
            title: `Тег группы для ${currentLogin} (пусто — убрать из группы)`,
            fields: [{ name: 'tag', label: 'Тег (напр. ivanov_ivan)', defaultValue: currentTag || '', type: 'text', autoFocus: true }],
            onSubmit: async ({ tag }) => {
                setInputModal(p => ({ ...p, isOpen: false }));
                try {
                    await api.put(`/admin/users/${userId}/special-conditions`, { clientTag: tag.trim() });
                    toastr.success(' ', tag.trim() ? `Тег группы: ${tag.trim()}` : 'Тег группы сброшен.');
                    fetchUsers();
                } catch (error) {
                    toastr.warning(' ', error.response?.data?.message || 'Не удалось обновить тег.');
                }
            }
        });
    };

    const handleSetBillingNotes = (userId, currentLogin, currentNotes) => {
        setInputModal({
            isOpen: true,
            title: `Заметки об оплате для ${currentLogin}`,
            fields: [{ name: 'notes', label: 'Заметки (видно только admin)', defaultValue: currentNotes || '', type: 'text', autoFocus: true }],
            onSubmit: async ({ notes }) => {
                setInputModal(p => ({ ...p, isOpen: false }));
                try {
                    await api.put(`/admin/users/${userId}/special-conditions`, { billingNotes: notes.trim() });
                    toastr.success(' ', 'Заметки обновлены.');
                    fetchUsers();
                } catch (error) {
                    toastr.warning(' ', error.response?.data?.message || 'Не удалось обновить заметки.');
                }
            }
        });
    };

    const handleToggleLock = async (userId) => {
        try {
            await api.put(`/admin/users/${userId}/toggle-lock`);
            fetchUsers();
        } catch (error) {
            toastr.warning(' ', error.response?.data?.message || 'Не удалось обновить статус блокировки.');
        }
    };

    const handleToggleFreeze = async (userId, userLogin, currentFrozen) => {
        try {
            await api.put(`/admin/users/${userId}/toggle-freeze`);
            toastr.success(
                ' ',
                currentFrozen
                    ? `Подписка ${userLogin} разморожена.`
                    : `Подписка ${userLogin} заморожена.`
            );
            fetchUsers();
        } catch (error) {
            toastr.warning(' ', error.response?.data?.message || 'Не удалось изменить статус заморозки.');
        }
    };

    const handleUpdateSubscription = (userId, currentLogin) => {
        setInputModal({
            isOpen: true,
            title: `Изменить подписку для ${currentLogin}`,
            fields: [
                { name: 'days', label: 'Дни (можно отрицательные)', defaultValue: '0', type: 'number' },
                { name: 'hours', label: 'Часы (можно отрицательные)', defaultValue: '0', type: 'number' },
                { name: 'minutes', label: 'Минуты (можно отрицательные)', defaultValue: '0', type: 'number' },
            ],
            onSubmit: async ({ days: d, hours: h, minutes: m }) => {
                setInputModal(p => ({ ...p, isOpen: false }));
                const days = parseInt(d, 10) || 0;
                const hours = parseInt(h, 10) || 0;
                const minutes = parseInt(m, 10) || 0;
                const totalMinutes = days * 24 * 60 + hours * 60 + minutes;
                if (totalMinutes === 0) {
                    toastr.info(' ', 'Изменение равно нулю, подписка не изменена.');
                    return;
                }
                try {
                    await api.put(`/admin/users/${userId}/subscription`, { days, hours, minutes });
                    toastr.success(' ', 'Подписка успешно обновлена.');
                    fetchUsers();
                } catch (error) {
                    toastr.warning(' ', error.response?.data?.message || 'Не удалось обновить подписку.');
                }
            }
        });
    };

    const handleBulkSubscription = () => {
        if (selectedUserIds.size === 0) return;
        setInputModal({
            isOpen: true,
            title: `Изменить подписку для ${selectedUserIds.size} пользователей`,
            fields: [
                { name: 'days', label: 'Дни (можно отрицательные)', defaultValue: '0', type: 'number' },
                { name: 'hours', label: 'Часы (можно отрицательные)', defaultValue: '0', type: 'number' },
                { name: 'minutes', label: 'Минуты (можно отрицательные)', defaultValue: '0', type: 'number' },
            ],
            onSubmit: async ({ days: d, hours: h, minutes: m }) => {
                setInputModal(p => ({ ...p, isOpen: false }));
                const days = parseInt(d, 10) || 0;
                const hours = parseInt(h, 10) || 0;
                const minutes = parseInt(m, 10) || 0;
                const totalMinutes = days * 24 * 60 + hours * 60 + minutes;
                if (totalMinutes === 0) {
                    toastr.info(' ', 'Изменение равно нулю.');
                    return;
                }
                try {
                    const res = await api.put('/admin/users/bulk-subscription', {
                        userIds: Array.from(selectedUserIds),
                        days,
                        hours,
                        minutes
                    });
                    toastr.success(' ', res.data.message || 'Подписка обновлена.');
                    setSelectedUserIds(new Set());
                    fetchUsers();
                } catch (error) {
                    toastr.warning(' ', error.response?.data?.message || 'Не удалось обновить подписку.');
                }
            }
        });
    };

    const handleUpdateSessions = (userId, currentLogin, currentMax) => {
        setInputModal({
            isOpen: true,
            title: `Лимит сессий для ${currentLogin} (сейчас: ${currentMax})`,
            fields: [{ name: 'max', label: 'Новый лимит сессий', defaultValue: String(currentMax), type: 'number', autoFocus: true }],
            onSubmit: async ({ max }) => {
                setInputModal(p => ({ ...p, isOpen: false }));
                const maxSessions = parseInt(max, 10);
                if (maxSessions && maxSessions > 0) {
                    try {
                        await api.put(`/admin/users/${userId}/sessions`, { maxSessions });
                        fetchUsers();
                    } catch (error) {
                        toastr.warning(' ', error.response?.data?.message || 'Не удалось обновить лимит сессий.');
                    }
                } else {
                    toastr.info(' ', 'Пожалуйста, введите корректное число (больше 0).');
                }
            }
        });
    };

    const handleShowSessions = async (login) => {
        try {
            const res = await api.get(`/admin/users/${encodeURIComponent(login)}/sessions`);
            setSessionsModal({
                isOpen: true,
                login: res.data.login,
                maxConcurrentSessions: res.data.maxConcurrentSessions || 0,
                sessions: res.data.sessions || []
            });
        } catch (error) {
            toastr.warning(' ', error.response?.data?.message || 'Не удалось получить список сессий.');
        }
    };

    const handleDeleteSession = async (login, sessionId) => {
        try {
            await api.delete(
                `/admin/users/${encodeURIComponent(login)}/sessions/${encodeURIComponent(sessionId)}`
            );

            const res = await api.get(`/admin/users/${encodeURIComponent(login)}/sessions`);
            setSessionsModal((prev) => ({
                ...prev,
                maxConcurrentSessions: res.data.maxConcurrentSessions || prev.maxConcurrentSessions,
                sessions: res.data.sessions || []
            }));

            fetchUsers();

            toastr.success(' ', 'Сессия успешно завершена.');
        } catch (error) {
            const msg = error.response?.data?.message || 'Не удалось завершить сессию.';
            toastr.warning(' ', msg);
        }
    };

    const handleDeleteUser = (userId, userLogin) => {
        setConfirmModal({
            isOpen: true,
            message: `Вы уверены, что хотите удалить пользователя "${userLogin}"?\n\nВНИМАНИЕ: Это действие необратимо и приведет к удалению всех устройств, привязанных к этому аккаунту.`,
            danger: true,
            onConfirm: async () => {
                setConfirmModal(p => ({ ...p, isOpen: false }));
                try {
                    await api.deleteUser(userId);
                    toastr.success(' ', `Пользователь ${userLogin} успешно удален.`);
                    setSelectedUserIds(prev => {
                        const next = new Set(prev);
                        next.delete(userId);
                        return next;
                    });
                    fetchUsers();
                } catch (error) {
                    toastr.warning(' ', `Ошибка: ${error.response?.data?.message || 'Не удалось удалить пользователя.'}`);
                }
            }
        });
    };

    const toggleSelectUser = (userId) => {
        setSelectedUserIds(prev => {
            const next = new Set(prev);
            if (next.has(userId)) {
                next.delete(userId);
            } else {
                next.add(userId);
            }
            return next;
        });
    };

    const selectableUsers = users.filter(u => u.role !== 'admin');
    const allSelected = selectableUsers.length > 0 && selectableUsers.every(u => selectedUserIds.has(u._id));

    const toggleSelectAll = () => {
        if (allSelected) {
            setSelectedUserIds(new Set());
        } else {
            setSelectedUserIds(new Set(selectableUsers.map(u => u._id)));
        }
    };

    return (
        <div className="user-management-container">
            <h2>Добавить нового пользователя</h2>
            {message && <p className="message-feedback">{message}</p>}
            <form onSubmit={handleCreateUser} className="add-user-form">
                <input
                    type="text"
                    placeholder="Логин"
                    value={newLogin}
                    onChange={(e) => setNewLogin(e.target.value)}
                    required
                />
                <input
                    type="password"
                    placeholder="Пароль"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                />
                <button type="submit">Создать пользователя</button>
            </form>

            <hr />

            <h2>Управление пользователями</h2>

            {selectedUserIds.size > 0 && (
                <div className="bulk-actions-bar">
                    <span className="bulk-count">Выбрано: {selectedUserIds.size}</span>
                    <button className="bulk-sub-btn" onClick={handleBulkSubscription}>
                        Изменить подписку ({selectedUserIds.size})
                    </button>
                    <button className="bulk-clear-btn" onClick={() => setSelectedUserIds(new Set())}>
                        Снять выбор
                    </button>
                </div>
            )}

            {selectableUsers.length > 0 && (
                <div className="select-all-row">
                    <label className="select-all-label">
                        <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={toggleSelectAll}
                        />
                        {allSelected ? 'Снять выбор со всех' : 'Выбрать всех пользователей'}
                    </label>
                </div>
            )}

            <div className="user-list">
                {users.map((user) => {
                    const subInfo = formatSubDate(user.subscriptionExpiresAt);
                    const isNonAdmin = user.role !== 'admin';
                    const isSelected = selectedUserIds.has(user._id);
                    return (
                        <div
                            key={user._id}
                            className={`user-card${isSelected ? ' user-card--selected' : ''}`}
                        >
                            <div className="user-info">
                                {isNonAdmin && (
                                    <input
                                        type="checkbox"
                                        className="user-checkbox"
                                        checked={isSelected}
                                        onChange={() => toggleSelectUser(user._id)}
                                    />
                                )}
                                <span className="user-login">{user.login}</span>
                                <span
                                    className={`status-badge ${
                                        user.isOnline ? 'online' : 'offline'
                                    }`}
                                >
                                    {user.isOnline ? 'Online' : 'Offline'}
                                </span>
                                <span
                                    className={`status-badge ${
                                        user.isLocked ? 'locked' : 'active'
                                    }`}
                                >
                                    {user.isLocked ? 'Заблокирован' : 'Активен'}
                                </span>
                                {isNonAdmin && user.subscriptionFrozen && (
                                    <span className="status-badge frozen">Заморожена</span>
                                )}
                                {isNonAdmin && (
                                    <span
                                        className="status-badge"
                                        style={{
                                            background: user.chatAccess ? '#28a745' : '#6c757d',
                                            color: '#fff'
                                        }}
                                        title={user.chatAccess ? 'Есть доступ к приватному чату' : 'Нет доступа к чату'}
                                    >
                                        💬 {user.chatAccess ? 'есть' : 'нет'}
                                    </span>
                                )}
                                <span
                                    className={`sub-info ${
                                        subInfo.isExpired ? 'expired' : 'sub-active'
                                    }`}
                                >
                                    Подписка: {subInfo.text}
                                </span>
                                <span className="sub-info">
                                    Сессий: {user.sessionsCount ?? 0} из{' '}
                                    {user.maxConcurrentSessions || 1} (Актив.Сессий:{' '}
                                    {user.activeSessionsCount ?? 0})
                                </span>
                            </div>
                            <div className="user-actions">
                                <button
                                    onClick={() =>
                                        handleUpdateSubscription(user._id, user.login)
                                    }
                                    className="sub-btn"
                                >
                                    Изменить подписку
                                </button>
                                {isNonAdmin && (
                                    <button
                                        onClick={() =>
                                            handleToggleFreeze(user._id, user.login, user.subscriptionFrozen)
                                        }
                                        className={user.subscriptionFrozen ? 'unfreeze-btn' : 'freeze-btn'}
                                    >
                                        {user.subscriptionFrozen ? 'Разморозить' : 'Заморозить'}
                                    </button>
                                )}
                                <button
                                    onClick={() => handleUpdateLogin(user._id, user.login)}
                                >
                                    Сменить логин
                                </button>
                                <button
                                    onClick={() => handleUpdatePassword(user._id)}
                                >
                                    Сменить пароль
                                </button>
                                <button
                                    onClick={() =>
                                        handleUpdateSessions(
                                            user._id,
                                            user.login,
                                            user.maxConcurrentSessions || 1
                                        )
                                    }
                                >
                                    Лимит сессий
                                </button>
                                <button
                                    onClick={() => handleToggleLock(user._id)}
                                    className={
                                        user.isLocked ? 'unlock-btn' : 'lock-btn'
                                    }
                                >
                                    {user.isLocked ? 'Разблокировать' : 'Заблокировать'}
                                </button>
                                <button
                                    onClick={() => handleShowSessions(user.login)}
                                >
                                    Сессии
                                </button>
                                {isNonAdmin && (
                                    <button
                                        onClick={() => handleToggleChatAccess(user._id, !!user.chatAccess, user.login)}
                                        style={{
                                            background: user.chatAccess ? '#5a2a2a' : '#28a745',
                                            color: '#fff'
                                        }}
                                    >
                                        {user.chatAccess ? 'Закрыть чат' : 'Открыть чат'}
                                    </button>
                                )}
                                {isNonAdmin && (
                                    <button
                                        onClick={() => handleToggleTransferAccess(user._id, !!user.canTransferDevices, user.login)}
                                        style={{
                                            background: user.canTransferDevices ? '#5a2a2a' : '#1a6ab1',
                                            color: '#fff'
                                        }}
                                    >
                                        {user.canTransferDevices ? 'Выкл. передачу' : 'Вкл. передачу'}
                                    </button>
                                )}
                                {isNonAdmin && (
                                    <button
                                        onClick={() => handleSetFixedPrice(user._id, user.login, user.fixedMonthlyPrice)}
                                        style={{
                                            background: (user.fixedMonthlyPrice !== null && user.fixedMonthlyPrice !== undefined) ? '#7b2fbe' : '#555',
                                            color: '#fff'
                                        }}
                                    >
                                        {(user.fixedMonthlyPrice !== null && user.fixedMonthlyPrice !== undefined) ? `💰 $${user.fixedMonthlyPrice}` : '💰 Цена'}
                                    </button>
                                )}
                                {isNonAdmin && (
                                    <button
                                        onClick={() => handleToggleSessionLock(user._id, user.login, user.sessionLocked)}
                                        style={{ background: user.sessionLocked ? '#be2f2f' : '#555', color: '#fff' }}
                                    >
                                        {user.sessionLocked ? '🔒 Сессии' : '🔓 Сессии'}
                                    </button>
                                )}
                                {isNonAdmin && (
                                    <button
                                        onClick={() => handleSetClientTag(user._id, user.login, user.clientTag)}
                                        style={{ background: (user.clientTag && user.clientTag !== '') ? '#2f7bbe' : '#555', color: '#fff' }}
                                    >
                                        {(user.clientTag && user.clientTag !== '') ? `🏷 ${user.clientTag}` : '🏷 Тег'}
                                    </button>
                                )}
                                {isNonAdmin && (
                                    <button
                                        onClick={() => handleSetBillingNotes(user._id, user.login, user.billingNotes)}
                                        style={{ background: (user.billingNotes && user.billingNotes !== '') ? '#2fbe7b' : '#555', color: '#fff' }}
                                    >
                                        {(user.billingNotes && user.billingNotes !== '') ? '📝 Заметка ✓' : '📝 Заметка'}
                                    </button>
                                )}
                                <button
                                    onClick={() =>
                                        handleDeleteUser(user._id, user.login)
                                    }
                                    className="delete-btn"
                                >
                                    Удалить
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            {sessionsModal.isOpen && (
                <div
                    className="sessions-modal-backdrop"
                    onClick={() =>
                        setSessionsModal((prev) => ({ ...prev, isOpen: false }))
                    }
                >
                    <div
                        className="sessions-modal"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3>Сессии пользователя {sessionsModal.login}</h3>
                        <p>
                            Лимит сессий:{' '}
                            {sessionsModal.maxConcurrentSessions || 0}
                        </p>

                        {sessionsModal.sessions.length === 0 ? (
                            <p>Активных сессий нет.</p>
                        ) : (
                            <table className="sessions-table">
                                <thead>
                                    <tr>
                                        <th>Session ID</th>
                                        <th>IP</th>
                                        <th>User-Agent</th>
                                        <th>Вход</th>
                                        <th>Последняя активность</th>
                                        <th>WS</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sessionsModal.sessions.map((s) => (
                                        <tr key={s.sessionId}>
                                            <td>{s.sessionId}</td>
                                            <td>{s.ip || '-'}</td>
                                            <td className="ua-cell">
                                                {s.userAgent || '-'}
                                            </td>
                                            <td>
                                                {s.loggedInAt
                                                    ? new Date(
                                                          s.loggedInAt
                                                      ).toLocaleString('ru-RU')
                                                    : '-'}
                                            </td>
                                            <td>
                                                {s.lastSeenAt
                                                    ? new Date(
                                                          s.lastSeenAt
                                                      ).toLocaleString('ru-RU')
                                                    : '-'}
                                            </td>
                                            <td
                                                style={{
                                                    color: s.wsConnected ? '#28a745' : '#6c757d',
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                {s.wsConnected ? 'Да' : 'Нет'}
                                            </td>
                                            <td>
                                                <button
                                                    className="session-kill-btn"
                                                    onClick={() =>
                                                        handleDeleteSession(
                                                            sessionsModal.login,
                                                            s.sessionId
                                                        )
                                                    }
                                                >
                                                    Завершить
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}

                        <div className="sessions-modal-actions">
                            <button
                                onClick={() =>
                                    setSessionsModal((prev) => ({
                                        ...prev,
                                        isOpen: false
                                    }))
                                }
                            >
                                Закрыть
                            </button>
                        </div>
                    </div>
                </div>
            )}

        <ConfirmModal
            isOpen={confirmModal.isOpen}
            message={confirmModal.message}
            danger={confirmModal.danger}
            onConfirm={confirmModal.onConfirm}
            onCancel={() => setConfirmModal(p => ({ ...p, isOpen: false }))}
        />
        <InputModal
            isOpen={inputModal.isOpen}
            title={inputModal.title}
            fields={inputModal.fields}
            onSubmit={inputModal.onSubmit}
            onCancel={() => setInputModal(p => ({ ...p, isOpen: false }))}
        />
        </div>
    );
}

export default UserManagement;
